import { Link, Text } from "north.js";
import React, { useState } from "react";
import styled from "styled-components";

import { useDeviceSize } from "src/shared/hooks";

// Safari's ITP blocks 3rd-party storage the Notion embed needs, which causes
// Notion's client to reload repeatedly, which crashes the whole tab and
// forces a full page reload. We track reload frequency in sessionStorage
// (first-party, unaffected by ITP) to detect the page repeatedly reloading
// quickly.
const RELOAD_KEY_PREFIX = "notion_embed_reload_tracker";
const MAX_RELOADS = 2;
const WINDOW_MS = 5000;

const shouldFallbackToLink = (url: string): boolean => {
  try {
    const key = `${RELOAD_KEY_PREFIX}_${url}`;
    const raw = sessionStorage.getItem(key);
    const now = Date.now();
    const data = raw ? JSON.parse(raw) : { count: 0, first: now };

    if (now - data.first > WINDOW_MS) {
      sessionStorage.setItem(key, JSON.stringify({ count: 1, first: now }));
      return false;
    }

    const updated = { count: data.count + 1, first: data.first };
    sessionStorage.setItem(key, JSON.stringify(updated));
    return updated.count > MAX_RELOADS;
  } catch {
    return false; // sessionStorage unavailable (private mode etc), fail open
  }
};

type NotionEmbedProps = {
  url: string;
  title: string;
  fallbackLinkLabel?: string;
  fallbackText?: React.ReactNode;
  height?: string;
};

const NotionEmbed: React.FC<NotionEmbedProps> = ({
  url,
  title,
  fallbackLinkLabel = "Learn more",
  fallbackText,
  height = "90vh",
}) => {
  const isTabletOrSmaller = useDeviceSize("tablet");
  const [useFallback] = useState(() => shouldFallbackToLink(url));

  if (isTabletOrSmaller || useFallback) {
    return (
      <FallbackContainer>
        <Text>
          <Link href={url} openInNewTab>
            {fallbackLinkLabel}
          </Link>{" "}
          {fallbackText}
        </Text>
      </FallbackContainer>
    );
  }

  return (
    <StyledIframe
      src={url}
      frameBorder="0"
      allowFullScreen
      title={title}
      height={height}
    />
  );
};

const StyledIframe = styled.iframe<{ height: string }>`
  display: block;
  width: 100%;
  height: ${({ height }) => height};
  margin: 0 auto;
  border: none;
`;

const FallbackContainer = styled.div`
  margin: 40px;

  ${({ theme }) => theme.mediaQueries.largeMobile`
    margin: 24px;
  `}
`;

export const FullPageNotionEmbed: React.FC<Omit<NotionEmbedProps, "height">> = (
  props
) => (
  <FullPageNotionBody>
    <NotionEmbed {...props} height="100%" />
  </FullPageNotionBody>
);

const FullPageNotionBody = styled.main`
  position: relative;
  flex: 1 1 auto;
  width: 100%;
  min-height: 0;
  padding: 0;
  background: ${({ theme }) => theme.globalConstants.color.white};

  > iframe {
    position: absolute;
    inset: 0;
  }
`;

export default NotionEmbed;
